import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// import '../css/StockReport.css'; // Removed custom CSS
import Sidebar from '../Dashboard/SideBar.js';
import Header from '../Dashboard/Header.js';
import API_BASE_URL from '../../config';
import { Box } from '@mui/material';

const StockReport = () => {
  const [formData, setFormData] = useState({
    department: '',
    group: '',
    brand: '',
    design: '',
    color: '',
    size: '',
    pattern: '',
    season: ''
  });

  const [suggestions, setSuggestions] = useState({
    department: [],
    group: [],
    brand: [],
    design: [],
    color: [],
    size: [],
    pattern: [],
    season: []
  });

  const [selectedIndex, setSelectedIndex] = useState(-1);  // Track the highlighted suggestion
  const suggestionRefs = useRef({});  // Store refs for each suggestion list
  const navigate = useNavigate();

  const userId = localStorage.getItem('userId');
  const accessToken = localStorage.getItem('accessToken');
  const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany'));

  // Validate early, but don't return early
  if (!userId || !accessToken) {
    console.error('Missing userId or accessToken in localStorage');
  }

  if (!selectedCompany || !selectedCompany.CompCode) {
    console.error('Company code is missing or invalid');
  }

  // Make sure the hooks are always called before the conditional returns.
  const CompCode = selectedCompany ? selectedCompany.CompCode : null;

  // Dynamic Fetch Function (fetch suggestions)
  const fetchSuggestions = async (field, searchTerm) => {
    if (!searchTerm) {
      setSuggestions((prev) => ({ ...prev, [field]: [] }));
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/getAll${field}s?CompCode=${CompCode}&${field}=${searchTerm}`, {
        headers: {
          'Content-Type': 'application/json',
          'UserId': userId,
          'Authorization': `Bearer ${accessToken}`
        }
      });

      setSuggestions((prev) => ({ ...prev, [field]: response.data }));
    } catch (error) {
      console.error(`Error fetching ${field} suggestions:`, error);
    }
  };

  // Handle input change and fetch corresponding suggestions
  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Reset selected index when typing a new value
    setSelectedIndex(-1);

    // Call fetchSuggestions dynamically based on the name of the field
    if (value) {
      await fetchSuggestions(name, value);
    }
  };

  // Handle suggestion click, store SRNO and show NAME in the input field
  const handleSuggestionClick = (field, suggestion) => {
    // Set SRNO in formData, but show NAME in the input field
    setFormData({
      ...formData,
      [field]: suggestion.NAME // Show the NAME in the input field
    });

    // Store SRNO for navigation
    setFormData((prevData) => ({
      ...prevData,
      [`${field}SRNO`]: suggestion.SRNO // Store the SRNO in the formData state
    }));

    // Clear suggestions after selection
    setSuggestions((prev) => ({ ...prev, [field]: [] }));
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Build query string with SRNO values from the formData
    const queryParams = new URLSearchParams({
      departmentcode: formData.departmentSRNO || '',  // Use the SRNO value for department
      groupcode: formData.groupSRNO || '',            // Use the SRNO value for group
      brandcode: formData.brandSRNO || '',            // Use the SRNO value for brand
      designcode: formData.designSRNO || '',          // Use the SRNO value for design
      colorcode: formData.colorSRNO || '',            // Use the SRNO value for color
      sizecode: formData.sizeSRNO || '',              // Use the SRNO value for size
      patterncode: formData.patternSRNO || '',        // Use the SRNO value for pattern
      seasoncode: formData.seasonSRNO || ''           // Use the SRNO value for season
    });

    // Navigate to the /stock page with query parameters
    navigate(`/stock?${queryParams.toString()}`);
  };

  // Render suggestion lists dynamically
  const renderSuggestions = (field) => {
    const fieldSuggestions = suggestions[field];
    if (fieldSuggestions.length > 0) {
      return (
        <ul className="absolute z-10 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {fieldSuggestions.map((suggestion, index) => (
            <li
              key={suggestion.SRNO}
              onClick={() => handleSuggestionClick(field, suggestion)}
              className={`px-4 py-2 cursor-pointer text-gray-700 transition-colors border-b border-gray-100 last:border-0 ${selectedIndex === index ? 'bg-blue-50 text-blue-700' : 'hover:bg-blue-50'
                }`}
              ref={(el) => suggestionRefs.current[index] = el}  // Set ref for each suggestion item
            >
              {suggestion.NAME} {/* Show the NAME to the user */}
            </li>
          ))}
        </ul>
      );
    }
  };

  // Handle keyboard navigation (ArrowUp, ArrowDown, Enter)
  const handleKeyDown = (e, field) => {
    const fieldSuggestions = suggestions[field];
    if (fieldSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, fieldSuggestions.length - 1)); // Move down
    } else if (e.key === 'ArrowUp') {
      setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0)); // Move up
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      // Select the highlighted suggestion on Enter key press
      e.preventDefault();  // Prevent form submission
      handleSuggestionClick(field, fieldSuggestions[selectedIndex]);
    }
  };

  // Scroll the selected item into view when selectedIndex changes
  useEffect(() => {
    if (selectedIndex >= 0 && suggestionRefs.current[selectedIndex]) {
      suggestionRefs.current[selectedIndex].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedIndex]);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth <= 768);

  const handleSidebarToggle = useCallback((collapsed) => {
    setIsSidebarCollapsed(collapsed);
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100 font-sans">
      <Sidebar onToggle={handleSidebarToggle} />
      <Box
        component="main"
        className={`flex-1 flex flex-col h-full transition-all duration-300 ${isSidebarCollapsed ? 'ml-0' : 'ml-0 md:ml-[260px]'}`}
      >
        <Header />
        <div className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-4 md:p-6">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h1 className="text-2xl font-bold text-center">Stock Report</h1>
              <p className="text-center mt-1 text-sm">Generate detailed stock reports based on various criteria</p>
            </div>

            <div className="p-8">
              <form autoComplete="off" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {['department', 'group', 'brand', 'design', 'color', 'size', 'pattern', 'season'].map((field) => (
                    <div key={field} className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {field.charAt(0).toUpperCase() + field.slice(1)}
                      </label>
                      <input
                        type="text"
                        name={field}
                        value={formData[field]}  // Show the NAME value to the user
                        onChange={handleChange}
                        onKeyDown={(e) => handleKeyDown(e, field)}  // Attach keydown handler
                        placeholder={`Enter ${field.charAt(0).toUpperCase() + field.slice(1)}`}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none transition-colors text-gray-700"
                      />
                      {renderSuggestions(field)}  {/* Render suggestions below the input */}
                    </div>
                  ))}
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium text-sm hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    Generate Report
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Box>
    </div>
  );
};

export default StockReport;
